/** Angular imports */
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

/** Core imports */
import { MediaLibraryUploadDto } from '@core/dto';

/** Shared imports */
import { BaseQueryParams } from '@shared';
import { buildQueryParams } from '@shared/utils';
import { ApiGetAllResponse, ApiSingleResponse, DashboardCacheType } from '../interfaces';
import { SharedService } from '../shared/shared.service';
import {
    ApiContent,
    ApiContentStats,
    AssetStats,
    SuggestNameResponse,
} from './media-library.interface';

@Injectable({
    providedIn: 'root',
})
export class MediaLibraryService {
    /**
     * Inject http client for api calls
     */
    private readonly _http = inject(HttpClient);

    /**
     * Shared service for common cache helpers.
     */
    private readonly _sharedService = inject(SharedService);

    /**
     * Base endpoint for media library routes
     */
    private readonly _baseEndpoint = '/api/media-library';

    /**
     * Internal cache for stats requests.
     */
    private readonly _cache = new Map<string, Observable<DashboardCacheType>>();

    /**
     * Retrieves the dashboard stats payload from the media library endpoint.
     */
    public getStats(): Observable<AssetStats> {
        return this._sharedService.getCached(this._cache, 'stats', () =>
            this._http.get<AssetStats>(`${this._baseEndpoint}/stats`),
        );
    }

    /**
     * Retrieves content stats
     */
    public getContentsStats(): Observable<ApiSingleResponse<ApiContentStats>> {
        return this._http.get<ApiSingleResponse<ApiContentStats>>(`${this._baseEndpoint}/stats`);
    }

    /**
     * Retrieves paginated contents for the media library
     */
    public getContents(params?: BaseQueryParams): Observable<ApiGetAllResponse<ApiContent>> {
        const queryParams = buildQueryParams(params);
        return this._http.get<ApiGetAllResponse<ApiContent>>(`${this._baseEndpoint}`, {
            params: queryParams,
        });
    }

    /**
     * Retrieves floating contents by passing a query parameter floating=true
     * Contents that are not assigned to any dealer
     *
     * @param dealerId - An optional dealer id to fetch both floating contents and contents assigned to the given dealerId
     *
     * The purpose of such is that it allows admins to easily fetch both contents (floating and assigned)
     * when creating a new playlist
     */
    public getFloatingContents(
        dealerId: string | null,
        params?: BaseQueryParams,
    ): Observable<ApiGetAllResponse<ApiContent>> {
        let httpParams = buildQueryParams(params);
        httpParams = httpParams.set('floating', 'true');
        if (dealerId) {
            httpParams = httpParams.set('dealerId', dealerId);
        }

        return this._http.get<ApiGetAllResponse<ApiContent>>(`${this._baseEndpoint}`, {
            params: httpParams,
        });
    }

    /**
     * Retrieves paginated contents assigned to a specific dealer.
     *
     * @param dealerId - ID of the dealer to retrieve contents for
     *
     * @returns Observable of paginated list of contents assigned to a specific dealer
     */
    public getDealerContents(
        dealerId: string,
        params?: BaseQueryParams,
    ): Observable<ApiGetAllResponse<ApiContent>> {
        const queryParams = buildQueryParams({
            ...params,
            filters: {
                'contentAssignments.dealerId': dealerId,
            },
        });
        return this._http.get<ApiGetAllResponse<ApiContent>>(`${this._baseEndpoint}`, {
            params: queryParams,
        });
    }

    /**
     * Retreives single content by its id
     *
     * @param contentId The media content id to retrieve
     */
    public getContentById(contentId: string): Observable<ApiSingleResponse<ApiContent>> {
        return this._http.get<ApiSingleResponse<ApiContent>>(`${this._baseEndpoint}/${contentId}`);
    }

    /**
     * Search content by fileName prefix.
     * Allowing us to prevent duplicate uploads by identifying existing files and their variations.
     *
     * @param fileName - The filename prefix to look for
     * @param dealerId - An optional dealer Id allows us to search for contents assigned to a specific dealer, else we search for floating contents
     */
    public searchContentByFileName(
        fileName: string,
        dealerId?: string,
    ): Observable<ApiGetAllResponse<ApiContent>> {
        const dealerContentFilters = dealerId
            ? { 'contentAssignments.dealerId': { _eq: dealerId } }
            : {};
        const queryParams = buildQueryParams({
            filters: { title: { _ilike: `${fileName}` }, ...dealerContentFilters },
            limit: 100,
        });
        return this._http.get<ApiGetAllResponse<ApiContent>>(`${this._baseEndpoint}`, {
            params: queryParams,
        });
    }

    /**
     * Requests a non-conflicting title and fileName suggestion from the backend.
     * Used to auto-resolve duplicate names before upload.
     *
     * @param title - The desired media title.
     * @param fileType - The mime type of the file (e.g. `image/png`).
     * @param dealerId - Optional dealer id to scope the uniqueness check to a specific dealer's library.
     * @returns Observable of the suggested title and fileName.
     */
    public suggestName(
        title: string,
        fileType: string,
        dealerId?: string | null,
    ): Observable<SuggestNameResponse> {
        let params = new HttpParams().set('title', title).set('fileType', fileType);
        if (dealerId) {
            params = params.set('dealerId', dealerId);
        }
        return this._http
            .get<ApiSingleResponse<SuggestNameResponse>>(`${this._baseEndpoint}/suggest-name`, {
                params,
            })
            .pipe(map((res) => res.data));
    }

    /**
     * Persists a single media item's metadata to the database.
     * This method should be called after a successful Filestack upload. It sends
     * the file's metadata and CDN URL to the backend for storage.
     *
     * @param mediaContent - The metadata and handle for a single uploaded media item.
     */
    public uploadContent(
        mediaContent: MediaLibraryUploadDto,
    ): Observable<ApiSingleResponse<ApiContent>> {
        return this._http.post<ApiSingleResponse<ApiContent>>(
            `${this._baseEndpoint}`,
            mediaContent,
        );
    }

    /**
     * Updates an existing media content item
     *
     * @param contentId - The ID of the content to update
     * @param data - The partial content data to update
     */
    public updateContent(
        contentId: string,
        data: Partial<ApiContent>,
    ): Observable<ApiSingleResponse<ApiContent>> {
        return this._http.patch<ApiSingleResponse<ApiContent>>(
            `${this._baseEndpoint}/${contentId}`,
            data,
        );
    }

    /**
     * Bulk deletes contents by their IDs.
     *
     * @param contentIds - List of content IDs to delete.
     * @returns An observable that emits a response with a confirmation message upon success.
     *
     * @example
     * this.deleteContents(['id-1', 'id-2']).subscribe(({ data }) => {
     *   console.log(data.message); // "Contents id-1, id-2 and their mappings deleted successfully"
     * });
     */
    public deleteContents(
        contentIds: string[],
    ): Observable<ApiSingleResponse<{ message: string }>> {
        return this._http.post<ApiSingleResponse<{ message: string }>>(
            `${this._baseEndpoint}/bulk-delete`,
            {
                contentIds,
            },
        );
    }
}
